import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Search, User, Check, X, RotateCcw, CheckCircle2, Ban, Filter, Lock, Unlock } from "lucide-react";
import { api, fileUrl, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import SearchableSelect from "../components/SearchableSelect";
import { fmtTgl } from "./PresensiListPage";
import { toast } from "sonner";
import { FotoLightbox } from "../components/FotoLightbox";

const inisial = (nama) => (nama || "").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((k) => k[0].toUpperCase()).join("");

const STATUS_CLS = { Hadir: "bg-emerald-500 text-white border-emerald-500", "Tidak Hadir": "bg-red-500 text-white border-red-500", "Belum Dicatat": "bg-white text-slate-500 border-slate-200" };

export default function PresensiSesiPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, can } = useAuth();
  const canWrite = can("presensi:write");
  const [sesi, setSesi] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [items, setItems] = useState([]);
  const [opts, setOpts] = useState({ komisi: [], status_kehadiran: [], bisa_koreksi: false });
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fKomisi, setFKomisi] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [busy, setBusy] = useState(null);

  const loadSesi = useCallback(() => api.get(`/presensi/sesi/${id}`).then((r) => setSesi(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  const loadItems = useCallback(async () => {
    const params = { page_size: 500 };
    if (search) params.search = search;
    if (fStatus) params.status = fStatus;
    if (fKomisi) params.komisi = fKomisi;
    try { const r = await api.get(`/presensi/sesi/${id}/kehadiran`, { params }); setItems(r.data.items); } catch (e) { toast.error(errMsg(e)); }
  }, [id, search, fStatus, fKomisi]);

  useEffect(() => { loadSesi(); api.get("/presensi/options").then((r) => setOpts(r.data)).catch(() => {}); }, [loadSesi]);
  useEffect(() => { const t = setTimeout(loadItems, 250); return () => clearTimeout(t); }, [loadItems]);

  const editable = canWrite && sesi && sesi.status !== "Dibatalkan";
  // Lock per-jemaat: catatan petugas lain terkunci kecuali sesi dibuka untuk koreksi
  const terkunci = (it) => !sesi?.mode_koreksi && it.status_kehadiran !== "Belum Dicatat" && it.dicatat_oleh && it.dicatat_oleh.id !== user?.id;
  const toggleKoreksi = async () => {
    try { const r = await api.put(`/presensi/sesi/${id}/koreksi`, { aktif: !sesi.mode_koreksi }); setSesi(r.data); toast.success(r.data.mode_koreksi ? "Sesi dibuka untuk koreksi — semua catatan dapat diubah" : "Mode koreksi ditutup — kunci per-jemaat aktif kembali"); } catch (e) { toast.error(errMsg(e)); }
  };

  const setStatus = async (jemaatId, status) => {
    if (!editable) return;
    const it = items.find((x) => x.jemaat_id === jemaatId);
    if (it && terkunci(it)) return toast.error(`Sudah dicatat oleh ${it.dicatat_oleh?.nama || it.dicatat_oleh?.username}. Buka sesi untuk koreksi bila perlu diubah.`);
    setBusy(jemaatId);
    const prev = items;
    setItems((it) => it.map((x) => (x.jemaat_id === jemaatId ? { ...x, status_kehadiran: status, dicatat_oleh: { id: user.id, nama: user.nama } } : x)));
    try {
      const r = await api.patch(`/presensi/sesi/${id}/kehadiran/${jemaatId}`, { status });
      setSesi((s) => ({ ...s, stats: r.data.stats }));
    } catch (e) { setItems(prev); toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const toggleHadir = (it) => setStatus(it.jemaat_id, it.status_kehadiran === "Hadir" ? "Belum Dicatat" : "Hadir");

  const finalize = async (status) => {
    try { const r = await api.put(`/presensi/sesi/${id}`, { status }); setSesi(r.data); toast.success(status === "Selesai" ? "Presensi disimpan & ditandai selesai" : "Sesi dibuka kembali"); } catch (e) { toast.error(errMsg(e)); }
  };
  const batal = async () => {
    if (!window.confirm("Batalkan sesi presensi ini? Data tetap tersimpan sebagai histori.")) return;
    try { await api.patch(`/presensi/sesi/${id}/batal`); toast.success("Sesi dibatalkan"); loadSesi(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!sesi) return <div className="text-center py-16 text-slate-400">Memuat...</div>;
  const st = sesi.stats;

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-24" data-testid="presensi-sesi-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => navigate("/presensi")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold tracking-tight text-slate-900 truncate" data-testid="sesi-judul">{sesi.komisi || sesi.jenis_ibadah}</h1>
          <p className="text-xs sm:text-sm text-slate-500">{fmtTgl(sesi.tanggal)}{sesi.komisi ? ` · ${sesi.jenis_ibadah}` : ""} · <span data-testid="sesi-status">{sesi.status}</span>{sesi.mode_koreksi && <span className="ml-2 inline-flex items-center gap-1 text-amber-700 font-semibold" data-testid="sesi-mode-koreksi"><Unlock className="h-3 w-3" /> Mode koreksi</span>}</p>
        </div>
        {opts.bisa_koreksi && sesi.status !== "Dibatalkan" && <Button variant="outline" size="sm" className={`ml-auto rounded-xl min-h-[40px] ${sesi.mode_koreksi ? "border-amber-400 text-amber-700 bg-amber-50" : ""}`} onClick={toggleKoreksi} data-testid="toggle-koreksi-btn">{sesi.mode_koreksi ? <><Lock className="h-4 w-4 mr-1" />Tutup Koreksi</> : <><Unlock className="h-4 w-4 mr-1" />Buka Koreksi</>}</Button>}
      </div>

      <Card className="p-3 sm:p-4 rounded-2xl sticky top-[64px] z-30 bg-white/95 backdrop-blur border-slate-200/80 shadow-sm" data-testid="sesi-stats">
        <div className="grid grid-cols-4 gap-2 text-center">
          <S label="Jemaat" v={st.total_jemaat} testId="stat-total" />
          <S label="Hadir" v={st.hadir} cls="text-emerald-700" testId="stat-hadir" />
          <S label="Tidak" v={st.tidak_hadir} cls="text-red-600" testId="stat-tidak-hadir" />
          <S label="Belum" v={st.belum_dicatat} cls="text-slate-500" testId="stat-belum" />
        </div>
        <div className="mt-2 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full transition-[width]" style={{ width: `${st.persentase_kehadiran}%` }} /></div>
          <span className="text-xs font-semibold text-slate-700" data-testid="stat-persen">{st.persentase_kehadiran}%</span>
        </div>
      </Card>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari NIJ / nama..." className="pl-9 min-h-[48px] rounded-xl" data-testid="kehadiran-search" />
        </div>
        <Button variant="outline" className="rounded-xl min-h-[48px]" onClick={() => setShowFilter((s) => !s)} data-testid="toggle-filter-btn"><Filter className="h-4 w-4" /></Button>
      </div>
      {showFilter && (
        <div className="grid grid-cols-2 gap-2">
          <SearchableSelect options={opts.status_kehadiran} value={fStatus} onChange={setFStatus} placeholder="Status kehadiran" testId="filter-status" />
          <SearchableSelect options={opts.komisi} value={fKomisi} onChange={setFKomisi} placeholder="Komisi" testId="filter-komisi" />
        </div>
      )}

      <div className="space-y-2" data-testid="kehadiran-list">
        {items.length === 0 && <p className="text-center text-slate-400 py-8" data-testid="kehadiran-empty">Tidak ada jemaat sesuai pencarian.</p>}
        {items.map((it) => (
          <Card key={it.jemaat_id} className={`p-3 rounded-2xl border ${it.status_kehadiran === "Hadir" ? "border-emerald-200 bg-emerald-50/40" : it.status_kehadiran === "Tidak Hadir" ? "border-red-200 bg-red-50/40" : "border-slate-200/80"}`} data-testid={`kehadiran-row-${it.jemaat_id}`}>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-3 flex-1 min-w-0 text-left" onClick={() => toggleHadir(it)} disabled={!editable || terkunci(it)} data-testid={`toggle-hadir-${it.jemaat_id}`}>
                <Avatar className={`h-10 w-10 rounded-xl border border-slate-200 shrink-0 ${it.foto_path ? "cursor-zoom-in hover:ring-2 hover:ring-[hsl(224,64%,33%)]/40" : ""}`} data-testid={`kehadiran-foto-${it.jemaat_id}`}
                  role={it.foto_path ? "button" : undefined} title={it.foto_path ? "Klik untuk memperbesar foto" : undefined}
                  onClick={(e) => { if (it.foto_path) { e.stopPropagation(); setZoom({ src: fileUrl(it.foto_path), alt: it.nama_lengkap }); } }}>
                  {it.foto_path ? <AvatarImage src={fileUrl(it.foto_path)} alt={it.nama_lengkap} className="object-cover" /> : null}
                  <AvatarFallback className="rounded-xl bg-[hsl(224,64%,33%)]/10 text-[hsl(224,64%,33%)] text-xs font-semibold" data-testid={`kehadiran-inisial-${it.jemaat_id}`}>{inisial(it.nama_lengkap) || <User className="h-4 w-4" />}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{it.nama_lengkap}</div>
                  <div className="text-xs text-slate-500 font-mono truncate">{it.nij}{it.nomor_kk ? ` · KK ${it.nomor_kk}` : ""}</div>
                  <div className="text-[11px] text-slate-500 truncate">{it.jenis_kelamin}{it.komisi ? ` · ${it.komisi}` : ""}{it.dicatat_oleh && it.status_kehadiran !== "Belum Dicatat" ? ` · oleh ${it.dicatat_oleh.nama || it.dicatat_oleh.username}` : ""}</div>
                </div>
              </button>
              {terkunci(it) && <Lock className="h-3.5 w-3.5 text-slate-400 shrink-0" title="Terkunci: dicatat petugas lain" data-testid={`lock-${it.jemaat_id}`} />}
              <span className={`text-[11px] px-2 py-1 rounded-full border font-semibold whitespace-nowrap ${STATUS_CLS[it.status_kehadiran]}`} data-testid={`status-${it.jemaat_id}`}>{it.status_kehadiran}</span>
            </div>
            {editable && !terkunci(it) && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                <BtnStatus active={it.status_kehadiran === "Hadir"} cls="emerald" icon={Check} label="Hadir" onClick={() => setStatus(it.jemaat_id, "Hadir")} disabled={busy === it.jemaat_id} testId={`btn-hadir-${it.jemaat_id}`} />
                <BtnStatus active={it.status_kehadiran === "Tidak Hadir"} cls="red" icon={X} label="Tidak Hadir" onClick={() => setStatus(it.jemaat_id, "Tidak Hadir")} disabled={busy === it.jemaat_id} testId={`btn-tidak-hadir-${it.jemaat_id}`} />
                <BtnStatus active={it.status_kehadiran === "Belum Dicatat"} cls="slate" icon={RotateCcw} label="Belum" onClick={() => setStatus(it.jemaat_id, "Belum Dicatat")} disabled={busy === it.jemaat_id} testId={`btn-belum-${it.jemaat_id}`} />
              </div>
            )}
          </Card>
        ))}
      </div>

      {canWrite && sesi.status !== "Dibatalkan" && (
        <div className="fixed bottom-16 md:bottom-4 left-0 right-0 z-30 px-4">
          <div className="max-w-3xl mx-auto flex gap-2">
            {sesi.status === "Draft" ? (
              <Button className="flex-1 min-h-[52px] rounded-2xl bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)] shadow-lg text-base" onClick={() => finalize("Selesai")} data-testid="selesai-btn"><CheckCircle2 className="h-5 w-5 mr-2" />Simpan & Selesai</Button>
            ) : (
              <Button variant="outline" className="flex-1 min-h-[52px] rounded-2xl bg-white shadow-lg" onClick={() => finalize("Draft")} data-testid="buka-kembali-btn"><RotateCcw className="h-4 w-4 mr-2" />Buka Kembali untuk Koreksi</Button>
            )}
            <Button variant="outline" className="min-h-[52px] rounded-2xl bg-white shadow-lg text-red-600" onClick={batal} data-testid="batal-sesi-btn"><Ban className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      <FotoLightbox src={zoom?.src} alt={zoom?.alt} onClose={() => setZoom(null)} />
    </div>
  );
}

const S = ({ label, v, cls = "text-slate-900", testId }) => (<div><div className={`text-xl sm:text-2xl font-bold ${cls}`} data-testid={testId}>{v}</div><div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div></div>);

const COLORS = {
  emerald: ["bg-emerald-500 text-white border-emerald-500", "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"],
  red: ["bg-red-500 text-white border-red-500", "bg-white text-red-600 border-red-200 hover:bg-red-50"],
  slate: ["bg-slate-600 text-white border-slate-600", "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"],
};
const BtnStatus = ({ active, cls, icon: Icon, label, onClick, disabled, testId }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testId} className={`min-h-[44px] rounded-xl border text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${COLORS[cls][active ? 0 : 1]}`}>
    <Icon className="h-4 w-4" />{label}
  </button>
);
